import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, type LucideIcon } from "lucide-react";

import Button from "@/components/Button";
import { useApp } from "@/App";
import { school } from "@/theme/school";
import { ROLES, type Role } from "@/data/mock";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — login.
//
// Two-step flow, both steps on one screen:
//
//   1. Pick a role (Director, Bursar, Teacher, Student, Parent).
//   2. Pick a name from that role's roster.
//
// There is no password field. This is a demo — the point is to
// show the role-aware UI, not to authenticate. A fake password
// input would be theatre. What we have instead is a real
// two-step selection that mirrors how a staff member would
// actually sign in to a shared school device: tap your name,
// and the app knows who you are.
//
// The whole screen is one column, centered, on the iOS grouped
// background. The brand mark is at the top, the role picker in
// the middle, the name list below, and the primary CTA pinned
// to the bottom above the home indicator.
// ─────────────────────────────────────────────────────────────────

export default function Login() {
  const signIn = useApp((s) => s.signIn);
  const mark = useApp((s) => s.mark);
  const [role, setRole] = useState<Role | null>(null);
  const [name, setName] = useState<string | null>(null);

  const roster = role ? school.roster[role] : [];
  const canContinue = Boolean(role && name);

  function handleContinue() {
    if (role && name) signIn(role, name);
  }

  return (
    <div className="flex h-full flex-col bg-ios-bg">
      {/* ─── Brand block ─────────────────────────────────────
          Top of the screen. Safe-area top padding keeps the
          mark below the notch/Dynamic Island on a real device. */}
      <div
        className="flex flex-col items-center px-6 pt-6 pb-2"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 32px)" }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 320,
            damping: 24,
            delay: 0.05,
          }}
          className="grid h-16 w-16 place-items-center rounded-ios-xl text-2xl font-bold tracking-tight shadow-ios-lg"
          style={{
            backgroundColor: "var(--brand-navy)",
            color: "var(--brand-gold)",
          }}
        >
          {mark}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, delay: 0.14, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mt-4 text-ios-title-2 font-bold text-ios-label"
        >
          {school.name}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mt-1 text-ios-footnote text-ios-secondary"
        >
          {school.motto}
        </motion.p>
      </div>

      {/* ─── Role picker ─────────────────────────────────────
          Scrollable so it survives an iPhone SE with a full
          roster. The picker is a 2-column grid of pill cards;
          each card has an icon, a label, and a checkmark when
          selected. This is denser than a vertical list, which
          matters when we have five roles. */}
      <div className="mt-6 flex-1 overflow-y-auto px-5 pb-4">
        <p className="mb-2 px-1 text-ios-caption-1 font-semibold uppercase tracking-wider text-ios-secondary">
          Sign in as
        </p>

        <div className="grid grid-cols-2 gap-2">
          {ROLES.map((r, i) => (
            <RoleCard
              key={r.key}
              role={r.key}
              label={r.label}
              Icon={r.Icon}
              selected={role === r.key}
              index={i}
              onSelect={() => {
                setRole(r.key);
                setName(null);
              }}
            />
          ))}
        </div>

        {/* ─── Roster ────────────────────────────────────────
            Appears below the role picker once a role is chosen.
            AnimatePresence + a keyed motion.div gives us a
            proper height/opacity entrance rather than a
            sudden layout jump. */}
        <AnimatePresence initial={false}>
          {role && (
            <motion.div
              key={role}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="overflow-hidden"
            >
              <p className="mb-2 mt-5 px-1 text-ios-caption-1 font-semibold uppercase tracking-wider text-ios-secondary">
                Who are you
              </p>

              <div className="overflow-hidden rounded-ios-lg bg-ios-card shadow-ios">
                {roster.map((person, i) => (
                  <button
                    key={person}
                    type="button"
                    onClick={() => setName(person)}
                    className={[
                      "ios-pressable flex w-full items-center gap-3 px-4 py-3 text-left",
                      i < roster.length - 1 ? "hairline-b" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[12px] font-semibold"
                      style={{
                        backgroundColor: "rgba(60, 60, 67, 0.08)",
                        color: "#3C3C43",
                      }}
                    >
                      {initials(person)}
                    </span>

                    <span className="min-w-0 flex-1 truncate text-ios-body text-ios-label">
                      {person}
                    </span>

                    {name === person && (
                      <motion.span
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 26 }}
                        className="grid h-5 w-5 shrink-0 place-items-center rounded-full"
                        style={{
                          backgroundColor: "var(--brand-navy)",
                          color: "#FFFFFF",
                        }}
                      >
                        <Check size={12} strokeWidth={3} absoluteStrokeWidth />
                      </motion.span>
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── CTA ─────────────────────────────────────────────
          Pinned above the home indicator. Safe-area bottom
          padding, plus a top hairline when the content
          scrolls under it. The button itself is disabled
          until both role and name are set. */}
      <div
        className="border-t border-ios-separator bg-ios-bg/95 px-5 pt-3 backdrop-blur"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
      >
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!canContinue}
          trailingIcon={ArrowRight}
          onClick={handleContinue}
        >
          Continue
        </Button>

        <p className="mt-3 text-center text-ios-caption-2 text-ios-secondary">
          Demo build · no password required
        </p>
      </div>
    </div>
  );
}

// ─── RoleCard ───────────────────────────────────────────────────

type RoleCardProps = {
  role: Role;
  label: string;
  Icon: LucideIcon;
  selected: boolean;
  index: number;
  onSelect: () => void;
};

function RoleCard({ label, Icon, selected, index, onSelect }: RoleCardProps) {
  return (
    <motion.button
      type="button"
      onClick={onSelect}
      initial={{ opacity: 0, y: 6 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.28,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: 0.26 + index * 0.04,
        },
      }}
      whileTap={{ scale: 0.97 }}
      className={[
        "ios-pressable relative flex items-center gap-2.5 rounded-ios-md px-3 py-3 text-left",
        selected ? "" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        backgroundColor: selected ? "var(--brand-navy)" : "#FFFFFF",
        color: selected ? "#FFFFFF" : "#1C1C1E",
        boxShadow: selected
          ? "0 4px 14px rgba(10, 37, 64, 0.24)"
          : "0 1px 2px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.04)",
      }}
    >
      <span
        className="grid h-8 w-8 shrink-0 place-items-center rounded-ios-md"
        style={{
          backgroundColor: selected
            ? "rgba(255, 255, 255, 0.14)"
            : "rgba(60, 60, 67, 0.08)",
          color: selected ? "var(--brand-gold)" : "#3C3C43",
        }}
      >
        <Icon size={16} strokeWidth={2} absoluteStrokeWidth />
      </span>

      <span className="truncate text-ios-subhead font-semibold">{label}</span>

      {selected && (
        <motion.span
          layoutId="role-check"
          className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full"
          style={{ backgroundColor: "var(--brand-gold)", color: "var(--brand-navy)" }}
          transition={{ type: "spring", stiffness: 420, damping: 30 }}
        >
          <Check size={11} strokeWidth={3.2} absoluteStrokeWidth />
        </motion.span>
      )}
    </motion.button>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase();
}